import { ErrorStatusEnum } from '@app/common';
import {
    Configuration,
    SymbolsApi,
    TradesApi,
    AuthApi,
    LogoApi,
    CurrenciesApi,
    PortfoliosApi,
    PortfoliosIntegrationsApi,
    ProfitsApi,
    AdminStatisticsApi,
    AdminCriticalNotificationsApi,
    InvestmentsApi,
    StripeApi,
    UsersApi,
    SymbolSplitsApi,
    TaxReportApi,
    StatisticsApi,
    SymbolDividendsApi,
    WatchlistsApi,
    TradesImportApi,
    TradesExportApi,
    SymbolAlternativesApi,
    LeaderboardApi,
    MailsApi,
    SymbolPriceAlertsApi,
    ResponseContext,
    AffiliateApi,
    AffiliatePayoutApi,
    FileApi,
    ImportLogTradesApi,
    PersonalAssetsApi,
    PersonalAssetsHistoryApi,
    PersonalAssetsScheduledApi,
    AuthExternalApi,
    EventLogApi,
    CriticalNotificationsApi,
    ImporterApi,
    SymbolFairPriceApi,
    CampaignLinksApi,
    ReferralApi,
    NotificationsApi,
} from '../defs/api';
import { OpenAPIConfig } from './open-api-config';
import { CookieService } from './services/cookie-service';
import { JsonError } from '../errors/json-error';
import { routes } from '../config/routes';

export type ApiResponseError = Error & {
    adsConsentState?: {
        advertisingConsent: boolean;
        generation: number;
    };
};

export const createApiResponseError = (
    url: string,
    status: number,
    payload: Record<string, unknown>,
): ApiResponseError => {
    const error = new Error(
        typeof payload.message === 'string' ? payload.message : 'Request failed',
    ) as ApiResponseError;
    const { pathname } = new URL(url, 'http://localhost');
    const { advertisingConsent } = payload;
    if (
        status === 409 &&
        pathname === '/api/users/ads-measurement' &&
        typeof advertisingConsent === 'boolean' &&
        Number.isInteger(payload.generation) &&
        Number(payload.generation) >= 0
    ) {
        error.adsConsentState = {
            advertisingConsent,
            generation: Number(payload.generation),
        };
    }
    return error;
};

const middleware = (cookieService: CookieService) => ({
    async post(context: ResponseContext): Promise<Response | void> {
        let json;

        if (![204].includes(context.response.status)) {
            try {
                json = await context.response.json();
            } catch (e) {
                throw new JsonError();
            }
        }

        if (![200, 201, 202, 204].includes(context.response.status)) {
            if (
                (context.response.status === 401 && json.message !== ErrorStatusEnum.BadCredentials) ||
                json.message === ErrorStatusEnum.Unauthorized
            ) {
                cookieService.setJwtToken(null);
                window.location.replace(routes.web.index);
                return;
            }

            if (context.response.status === 403) {
                window.location.replace(routes.web.index);
                return;
            }

            if (json) {
                const error = createApiResponseError(context.url, context.response.status, json);
                if (error.adsConsentState || json.message) throw error;
            }
            throw new Error(await context.response.text());
        }
    },
});

class ApiClient {
    public readonly configuration: Configuration;

    public readonly authController: AuthApi;

    public readonly authExternalApi: AuthExternalApi;

    public readonly portfolioController: PortfoliosApi;

    public readonly affiliateController: AffiliateApi;

    public readonly affiliatePayoutController: AffiliatePayoutApi;

    public readonly portfolioIntegrationController: PortfoliosIntegrationsApi;

    public readonly symbolController: SymbolsApi;

    public readonly tradeController: TradesApi;

    public readonly tradeImportController: TradesImportApi;

    public readonly importLogTradesApi: ImportLogTradesApi;

    public readonly tradeExportController: TradesExportApi;

    public readonly logoController: LogoApi;

    public readonly currencyController: CurrenciesApi;

    public readonly profitController: ProfitsApi;

    public readonly adminStatisticsController: AdminStatisticsApi;

    public readonly adminCriticalNotificationsController: AdminCriticalNotificationsApi;

    public readonly investmentController: InvestmentsApi;

    public readonly stripeController: StripeApi;

    public readonly userController: UsersApi;

    public readonly eventLogController: EventLogApi;

    public readonly symbolSplitsController: SymbolSplitsApi;

    public readonly taxReportController: TaxReportApi;

    public readonly statisticsController: StatisticsApi;

    public readonly symbolDividendsController: SymbolDividendsApi;

    public readonly watchlistsController: WatchlistsApi;

    public readonly criticalNotificationController: CriticalNotificationsApi;

    public readonly importerController: ImporterApi;

    public readonly symbolAlternativesController: SymbolAlternativesApi;

    public readonly leaderboardController: LeaderboardApi;

    public readonly mailController: MailsApi;

    public readonly symbolPriceAlertController: SymbolPriceAlertsApi;

    public readonly symbolFairPriceController: SymbolFairPriceApi;

    public readonly fileController: FileApi;

    public readonly personalAssetsApi: PersonalAssetsApi;

    public readonly personalAssetsHistoryApi: PersonalAssetsHistoryApi;

    public readonly personalAssetsScheduledApi: PersonalAssetsScheduledApi;

    public readonly campaignLinksController: CampaignLinksApi;

    public readonly referralController: ReferralApi;

    public readonly notificationsController: NotificationsApi;

    constructor(cookieService: CookieService, config?: Partial<OpenAPIConfig>) {
        this.configuration = new Configuration({
            accessToken: cookieService.getJwtToken(),
            basePath: config?.BASE ?? '',
            credentials: config?.CREDENTIALS ?? 'include',
        });

        const apiMiddleware = middleware(cookieService);

        this.authController = new AuthApi(this.configuration).withMiddleware(apiMiddleware);
        this.authExternalApi = new AuthExternalApi(this.configuration).withMiddleware(apiMiddleware);
        this.portfolioController = new PortfoliosApi(this.configuration).withMiddleware(apiMiddleware);
        this.portfolioIntegrationController = new PortfoliosIntegrationsApi(this.configuration).withMiddleware(
            apiMiddleware,
        );
        this.symbolController = new SymbolsApi(this.configuration).withMiddleware(apiMiddleware);
        this.fileController = new FileApi(this.configuration).withMiddleware(apiMiddleware);
        this.affiliateController = new AffiliateApi(this.configuration).withMiddleware(apiMiddleware);
        this.affiliatePayoutController = new AffiliatePayoutApi(this.configuration).withMiddleware(apiMiddleware);
        this.tradeController = new TradesApi(this.configuration).withMiddleware(apiMiddleware);
        this.tradeImportController = new TradesImportApi(this.configuration).withMiddleware(apiMiddleware);
        this.tradeExportController = new TradesExportApi(this.configuration).withMiddleware(apiMiddleware);
        this.importLogTradesApi = new ImportLogTradesApi(this.configuration).withMiddleware(apiMiddleware);
        this.logoController = new LogoApi(this.configuration).withMiddleware(apiMiddleware);
        this.currencyController = new CurrenciesApi(this.configuration).withMiddleware(apiMiddleware);
        this.profitController = new ProfitsApi(this.configuration).withMiddleware(apiMiddleware);
        this.adminStatisticsController = new AdminStatisticsApi(this.configuration).withMiddleware(apiMiddleware);
        this.adminCriticalNotificationsController = new AdminCriticalNotificationsApi(
            this.configuration,
        ).withMiddleware(apiMiddleware);
        this.investmentController = new InvestmentsApi(this.configuration).withMiddleware(apiMiddleware);
        this.stripeController = new StripeApi(this.configuration).withMiddleware(apiMiddleware);
        this.userController = new UsersApi(this.configuration).withMiddleware(apiMiddleware);
        this.eventLogController = new EventLogApi(this.configuration).withMiddleware(apiMiddleware);
        this.symbolSplitsController = new SymbolSplitsApi(this.configuration).withMiddleware(apiMiddleware);
        this.taxReportController = new TaxReportApi(this.configuration).withMiddleware(apiMiddleware);
        this.statisticsController = new StatisticsApi(this.configuration).withMiddleware(apiMiddleware);
        this.symbolDividendsController = new SymbolDividendsApi(this.configuration).withMiddleware(apiMiddleware);
        this.watchlistsController = new WatchlistsApi(this.configuration).withMiddleware(apiMiddleware);
        this.criticalNotificationController = new CriticalNotificationsApi(this.configuration).withMiddleware(
            apiMiddleware,
        );
        this.importerController = new ImporterApi(this.configuration).withMiddleware(apiMiddleware);
        this.symbolAlternativesController = new SymbolAlternativesApi(this.configuration).withMiddleware(apiMiddleware);
        this.leaderboardController = new LeaderboardApi(this.configuration).withMiddleware(apiMiddleware);
        this.mailController = new MailsApi(this.configuration).withMiddleware(apiMiddleware);
        this.symbolPriceAlertController = new SymbolPriceAlertsApi(this.configuration).withMiddleware(apiMiddleware);
        this.symbolFairPriceController = new SymbolFairPriceApi(this.configuration).withMiddleware(apiMiddleware);
        this.personalAssetsApi = new PersonalAssetsApi(this.configuration).withMiddleware(apiMiddleware);
        this.personalAssetsHistoryApi = new PersonalAssetsHistoryApi(this.configuration).withMiddleware(apiMiddleware);
        this.personalAssetsScheduledApi = new PersonalAssetsScheduledApi(this.configuration).withMiddleware(
            apiMiddleware,
        );
        this.campaignLinksController = new CampaignLinksApi(this.configuration).withMiddleware(apiMiddleware);
        this.referralController = new ReferralApi(this.configuration).withMiddleware(apiMiddleware);
        this.notificationsController = new NotificationsApi(this.configuration).withMiddleware(apiMiddleware);
    }
}

export default ApiClient;
